import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../components/button/button'
import PointerBackButton from '../components/NavigationPointers/PointerBackButton'
import { getColor, grey300, grey50, grey700, grey960, transparent } from '../constants/colors'
import { Images } from '../constants/paths'
import { AppConfig } from '../services/appConfig'
import { getText } from '../utils/dictionary'
import { getAsset } from '../utils/image'
import { captureScreenData, NON_BFF_SEGMENT_SCREENS } from '../utils/segmentAnalytics'
import LoginRemoteControlHandler from './handlers/loginRemoteControlHandler' // } //   LoginStates as LoginWithRemoteStates, //, {
import LoginSmartphoneHandler from './handlers/loginSmartphoneHandler'
import PageHandler from './handlers/pageHandler'

enum LoginStates {
  FocusButtons = 'FocusButtons',
  FocusTab = 'FocusTab',
  Error = 'Error',
}

export default class Login extends PageHandler {
  private _lastFocusedButton: Button
  private _lastEmail: string
  private _currentButton: Button
  private _selectedTab: ITab
  private _goBack: boolean
  private _goSteps: number

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: getColor(grey960),
      flex: {
        direction: 'column',
      },
      Logo: {
        w: 246,
        h: 28,
        y: 48,
        x: 48,
        texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_LARGE), 246, 28),
      },
      PointerBackButton: {
        type: PointerBackButton,
        x: (w) => w - 96,
        y: 68,
      },
      TopContainer: {
        h: 192,
        flex: {
          direction: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        },
        WithSmartphoneButton: {
          w: 300,
          type: Button,
          colorOverride: {
            fillFocused: getColor(grey50),
            labelFocused: getColor(grey960),
            fillUnfocused: getColor(grey960),
            labelUnfocused: getColor(grey300),
            accent: getColor(grey50),
          },
          label: getText('loginWithSmartphoneButton'),
        },
        WithRemoteControlButton: {
          flexItem: {
            marginLeft: 24,
          },
          w: 346,
          type: Button,
          colorOverride: {
            fillFocused: getColor(grey50),
            labelFocused: getColor(grey960),
            fillUnfocused: getColor(grey960),
            labelUnfocused: getColor(grey300),
            accent: getColor(grey50),
          },
          label: getText('loginWithRemoteControlButton'),
        },
      },
      MainContainer: {
        w: 1920,
        h: 888,
        rect: true,
        color: transparent,
      },
    }
  }

  get mainContainer() {
    return this.tag('MainContainer')
  }

  get buttonWithSmartphone() {
    return this.tag('WithSmartphoneButton') as Button
  }

  get buttonWithRemote() {
    return this.tag('WithRemoteControlButton') as Button
  }

  get LoginWithSmartphone() {
    return this.mainContainer.tag('LoginWithSmartphone') as LoginSmartphoneHandler
  }

  get LoginWithRemoteControl() {
    return this.mainContainer.tag('LoginWithRemoteControl') as LoginRemoteControlHandler
  }

  get goBack() {
    return this._goBack
  }

  get goSteps() {
    return this._goSteps
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  set params(v: { email: string; goBack?: boolean; goSteps?: number }) {
    this._goBack = v?.goBack ?? false
    this._goSteps = v?.goSteps ?? -1

    if (v?.email) {
      this._lastFocusedButton = this.buttonWithRemote
      this._lastEmail = v.email
    }
  }

  static _states() {
    return [
      class FocusButtons extends this {
        $enter() {
          this._currentButton = this._lastFocusedButton ?? this.buttonWithSmartphone
        }

        _getFocused() {
          return this._currentButton
        }

        _handleDown() {
          if (this._selectedTab.getFirstFocusElement()) {
            this._currentButton.patch({
              colorOverride: {
                fillUnfocused: getColor(grey700),
                labelUnfocused: getColor(grey50),
                accent: getColor(grey700),
              },
            })
            this._setState(LoginStates.FocusTab)
            this._selectedTab.focusFirstElement()
          }
        }
      },
      class FocusTab extends this {
        _getFocused() {
          return this._selectedTab as any as Lightning.Component
        }

        _handleUp() {
          if (this._selectedTab.isAtTopMostElement()) {
            this._setState(LoginStates.FocusButtons)
          }
        }

        // So it doesn't call the handleLeft/Right to change tabs
        _handleLeft(): void {}
        _handleRight(): void {}

        _handleEnter(): void {}
      },
      class Error extends this {
        $enter() {
          this._lastFocusedButton = this._currentButton
          this._currentButton = null
        }
      },
    ]
  }

  _active() {
    this._setState(LoginStates.FocusButtons)
    this.setLoadingAnimation(true)

    this._currentButton = this._currentButton ?? this.buttonWithSmartphone

    if (this._currentButton === this.buttonWithRemote) {
      this.loadRightPanel()
      this.LoginWithRemoteControl.email = this._lastEmail
    } else {
      this.loadLeftPanel()
    }

    this.setLoadingAnimation(false)
    this.setModal(false)

    void captureScreenData(NON_BFF_SEGMENT_SCREENS.Login)

    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _handleBack() {
    Router.back()
  }

  _handleRight() {
    if (this._currentButton === this.buttonWithSmartphone) {
      this.loadRightPanel()
    }
  }

  _handleLeft() {
    if (this._currentButton === this.buttonWithRemote) {
      this.loadLeftPanel()
    }
  }

  historyState(params) {
    if (params) {
      this._lastFocusedButton = this.tag(params.focusedButton) as Button
      this._lastEmail = params.email
    } else {
      return {
        email: this.LoginWithRemoteControl?.email,
        focusedButton:
          this._currentButton === this.buttonWithSmartphone
            ? 'WithSmartphoneButton'
            : 'WithRemoteControlButton',
      }
    }
  }

  loadRightPanel() {
    this.buttonWithSmartphone.patch({
      colorOverride: {
        fillFocused: getColor(grey50),
        labelFocused: getColor(grey960),
        fillUnfocused: getColor(grey960),
        labelUnfocused: getColor(grey300),
        accent: getColor(grey50),
      },
    })

    this.buttonWithRemote.patch({
      colorOverride: {
        fillUnfocused: getColor(grey700),
        labelUnfocused: getColor(grey50),
        accent: getColor(grey700),
      },
    })

    this._currentButton = this.buttonWithRemote

    this.mainContainer.patch({
      children: [
        {
          type: LoginRemoteControlHandler,
          ref: 'LoginWithRemoteControl',
          goBack: this.goBack,
          goSteps: this.goSteps,
        },
      ],
    })

    this._lastFocusedButton = this._currentButton
    this._selectedTab = this.LoginWithRemoteControl
  }

  loadLeftPanel() {
    this.buttonWithSmartphone.patch({
      colorOverride: {
        fillUnfocused: getColor(grey700),
        labelUnfocused: getColor(grey50),
        accent: getColor(grey700),
      },
    })

    this.buttonWithRemote.patch({
      colorOverride: {
        fillFocused: getColor(grey50),
        labelFocused: getColor(grey960),
        fillUnfocused: getColor(grey960),
        labelUnfocused: getColor(grey300),
        accent: getColor(grey50),
      },
    })

    this._currentButton = this.buttonWithSmartphone

    this.mainContainer.patch({
      children: [
        {
          type: LoginSmartphoneHandler,
          ref: 'LoginWithSmartphone',
          goBack: this.goBack,
          goSteps: this.goSteps,
        },
      ],
    })

    this._lastFocusedButton = this._currentButton
    this._selectedTab = this.LoginWithSmartphone
  }

  $returnFocusToParent() {
    this._currentButton = this.buttonWithRemote

    this._currentButton.patch({
      colorOverride: {
        fillFocused: getColor(grey50),
        labelFocused: getColor(grey960),
        fillUnfocused: getColor(grey960),
        labelUnfocused: getColor(grey300),
        accent: getColor(grey50),
      },
    })
    this._setState(LoginStates.FocusButtons)
  }

  $toggleLoading(enabled: boolean) {
    this.setLoadingAnimation(enabled)
  }

  $errorUserEmailPassword(data: { info: string; footer: string; retryAction: () => void }) {
    this._setState(LoginStates.Error)

    this.setModal(true, {
      ...data,
      wordWrap: true,
      buttons: [
        {
          label: getText('tryAgainButton'),
          callback: () => {
            data?.retryAction()
            this.setModal(false)
            this._setState(LoginStates.FocusTab)
          },
          isDefaultBackAction: true,
        },
        {
          label: getText('exit'),
          callback: this._handleBack,
        },
      ],
    })
  }

  $connectionError(data: { info: string; footer: string; retryAction: () => void }) {
    this._setState(LoginStates.Error)

    this.setModal(true, {
      ...data,
      wordWrap: true,
      buttons: [
        {
          label: getText('tryAgainButton'),
          callback: () => {
            data?.retryAction()
            this.setModal(false)
            this._setState(LoginStates.FocusTab)
          },
          isDefaultBackAction: true,
        },
        {
          label: getText('exit'),
          callback: () => {
            AppConfig.shared.onShutdown()
          },
        },
      ],
    })
    Router.focusWidget('Modal')
  }

  $smartPhoneError(data: { info: string; footer: string; retryAction: () => void }) {
    this._setState(LoginStates.Error)

    this.setModal(true, {
      ...data,
      wordWrap: true,
      buttons: [
        {
          label: getText('signInWithPassword'),
          callback: () => {
            Router.focusPage()
            this.loadRightPanel()
            this._setState(LoginStates.FocusButtons)
            this.setModal(false)
          },
        },
        {
          label: getText('exit'),
          callback: this._handleBack,
        },
      ],
    })
    Router.focusWidget('Modal')
  }

  $updateFocusByHover(button) {
    this._currentButton = button
    this._refocus()
  }

  _onCursorEnter() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _onCursorLeave() {
    if (this.state === LoginStates.FocusButtons) {
      if (this._selectedTab === this.LoginWithRemoteControl) {
        this._currentButton = this.buttonWithRemote
        this.buttonWithRemote._focus()
      } else {
        this._currentButton = this.buttonWithSmartphone
        this.buttonWithSmartphone._focus()
      }

      this._refocus()
    } else if (
      this.state === LoginStates.FocusTab &&
      this._selectedTab === this.LoginWithRemoteControl
    ) {
      ;(this._selectedTab as LoginRemoteControlHandler)?._setState(
        (this._selectedTab as LoginRemoteControlHandler)?.lastState,
      )
      ;(this.LoginWithRemoteControl?.getFocusedElement() as any)?._focus()
    }

    this.pointerBackButton.visible = false
  }

  _handleClick(button) {
    if (button === this.buttonWithSmartphone) {
      this._setState(LoginStates.FocusButtons)
      this.loadLeftPanel()
      this.buttonWithRemote._unfocus()
    } else if (button === this.buttonWithRemote) {
      this._setState(LoginStates.FocusButtons)
      this.loadRightPanel()
      this.buttonWithSmartphone._unfocus()
    } else if (this.state !== LoginStates.FocusTab) {
      this._setState(LoginStates.FocusTab)
    }
  }
}
